import { useSetState } from 'ahooks'
import { type FormProps, Button, Form, Loading, SafeArea } from 'antd-mobile'
import classNames from 'classnames'
import { isEmpty, isEqual } from 'lodash-es'
import { toJS } from 'mobx'
import { useLocalStore } from 'mobx-react'
import { observer, useLocalObservable } from 'mobx-react-lite'
import React, {
  Fragment,
  createContext,
  forwardRef,
  useContext,
  useImperativeHandle,
  useMemo,
  useState,
  useRef,
  useEffect
} from 'react'

import { SvgIcon } from '@/components'
import { type UnifiedStateStoreType } from '@/hooks'
import useElement from '@/hooks/useElement'
import authStore from '@/store/global/authStore'
import dictStore from '@/store/global/dictStore'
import screenShotStore from '@/store/global/screenShot'
import unifiedStore from '@/store/unified/unifiedStore'
import { isNullOrUndefined, commonNav, toPx } from '@/utils'

import { useEventManager } from '../EventManagerProvider'

import { ScreenShotEnum } from './constants'

import useFunctionCall from './hooks/useFunctionCall'
import RenderNode from './renderNode'
import { renderFormItem } from './utils'

import './index.less'

const dictStoreWithContext = createContext(dictStore)
interface EditFormprops extends Omit<FormProps, 'children'> {
  formInfo: any
  formInst?: any
  formButtons?: Array<Record<string, any>>
  formKey?: string
  initialValues?: any
  labelWidth?: number
  randomForm?: number
  formType?: string
  onFinish?: (values: any) => void
  onValuesChange?: (values: any) => void
  onFinishFailed?: (values: any) => void
  disabled?: boolean
  readed?: boolean
  convertFieldNames?: any
  stateStore?: UnifiedStateStoreType
}

const NewEditForm: React.FC<EditFormprops> = forwardRef((props, ref) => {
  const {
    formInfo,
    formInst,
    formButtons = [],
    formKey,
    initialValues,
    labelWidth,
    randomForm,
    onFinish,
    stateStore: propStateStore,
    onValuesChange,
    onFinishFailed,
    disabled,
    readed,
    formType = 'form',
    convertFieldNames,
    className = '',
    ...rest
  } = props
  const _stateStore = useContext(unifiedStore)
  const stateStore = propStateStore || _stateStore
  const [random, setRandom] = useState(1)
  const [formated, setFormated] = useState(undefined)
  const [state, setState] = useSetState<any>(null)
  const screenShotRef = useRef<any>(null)
  const store = useContext(dictStoreWithContext)
  const { getCodeFun } = useLocalStore(() => authStore)
  const { changeScreenShotRef } = useLocalStore(() => screenShotStore)
  const [form] = Form.useForm()
  const formInstance = formInst || form
  const { hideExec, onAfterChangeExec } = useFunctionCall({ form: formInstance })
  const setFieldsValue = async (values: any) => {
    const _values = await convertValue(values, null, convertFieldNames)
    console.log('_values:', _values)
    formInstance.setFieldsValue(_values)
    setRandom(Math.random())
  }
  const resetFields = () => {
    formInstance.resetFields()
  }
  useImperativeHandle(ref, () => ({ setFieldsValue }))
  const formSubmit = (values: any) => {
    const newValues = transform(values)
    console.log('transform:', newValues, convertValue, transform)
    onFinish?.(newValues)
  }
  const formSubmitFailed = (errors: any) => {
    if (onFinishFailed?.(errors)) {
      const newValues = transform(formInstance.getFieldsValue())
      onFinish?.(newValues)
    }
    const _errors = errors.errorFields
    // 不可见不校验
    formInfo?.map((info: any) => {
      info?.form_list?.map((item: any) => {
        const isHidden = item.hidden && hideExec(item?.hidden, state, {})
        const errorIndex = _errors.findIndex((e: any) =>
          isEqual(e.name, item.formItemProps?.name || item.name)
        )
        if (errorIndex > -1 && isHidden) {
          _errors.splice(errorIndex, 1)
        }
      })
    })
    const noError = _errors.length === 0
    const newValues = transform(formInstance.getFieldsValue())
    noError && onFinish?.(newValues)
  }
  const formButtonPreset: any = {
    goback: { className: 'default', title: '取消', onClick: () => commonNav.navigateBack() },
    goback2: {
      className: 'default',
      title: '取消',
      onClick: () => commonNav.navigateBack({ delta: 2 })
    },
    gohome: {
      className: 'default',
      title: '取消',
      onClick: () => window.close()
    },
    submit: {
      className: 'info',
      title: '提交',
      onClick: () => {
        formInstance.submit()
      }
    }
  }
  const getFormItemProps = (x: any) => {
    const elementRequired = !isNullOrUndefined(x?.formItemProps?.required)
      ? x?.formItemProps?.required
      : x?.required
    const elementLabel = !isNullOrUndefined(x?.formItemProps?.label)
      ? x?.formItemProps?.label
      : x?.label
    const elementName = x.name || x?.formItemProps?.name
    const formItemProps = {
      name: elementName,
      ...renderFormItem(x.type),
      ...x.formItemProps,
      className: classNames(x.formItemProps?.className, {
        'class-e': x?.disabled || x.formItemProps?.disabled,
        hidden: hideExec(x?.hidden, state, {})
      }),
      disabled: x?.disabled || x.formItemProps?.disabled,
      label: elementLabel,
      ...(readed ? { required: false } : {})
    }
    if (
      !isEmpty(elementRequired) ||
      Object.prototype.toString.call(elementRequired).slice(8, -1) === 'Boolean'
    ) {
      const _required = !readed && hideExec(elementRequired, state, {})
      const propRules = x?.formItemProps?.rules || []
      const requiredRule = {
        required: _required,
        message: `${elementLabel}不能为空`
      }
      formItemProps.rules = [_required && requiredRule, ...propRules]
        .filter(Boolean)
        .map((rule: any) => {
          if (rule.type === 'mobile') {
            return {
              ...rule,
              type: 'string',
              pattern: /^1[3-9]\d{9}$/
            }
          }
          return rule
        })
      formItemProps.required = _required
    }
    return formItemProps
  }
  const getElementProps = (x: any) => {
    if (x.type === 'DatePicker') {
      console.log('x---DatePicker', x)
    }
    // 过滤掉label, name, type, placeholder, formItemProps, 其他属性赋值给elementProps
    const {
      label,
      name,
      type,
      required,
      placeholder,
      formItemProps,
      elementProps: _elementProps,
      ...restElementProps
    } = x

    const elementProps = {
      readed,
      required: required || formItemProps?.required,
      ...restElementProps,
      ..._elementProps,
      form: formInstance,
      formProps: {
        name: name || formItemProps?.name,
        type
      },
      globalProps: { state: {}, option: {}, onAfterChangeExec }
    }
    const elementDisabled = !isNullOrUndefined(x?.elementProps?.disabled)
      ? x?.elementProps?.disabled
      : x?.disabled
    if (
      disabled ||
      !isEmpty(elementDisabled) ||
      Object.prototype.toString.call(elementDisabled).slice(8, -1) === 'Boolean'
    ) {
      elementProps.disabled = disabled || hideExec(elementDisabled, state, {})
    }
    if (x?.elementProps?.dictionary || x?.dictionary) {
      const dictionary_list =
        store.getOptions(
          x?.elementProps?.dictionary || x?.dictionary,
          x?.elementProps?.disabledOptions || x?.disabledOptions
        ) || []
      const options = toJS(dictionary_list)
      console.log('dict=', options)
      elementProps.dataList = options
      elementProps.options = options
    }
    return elementProps
  }
  const { convertValue, transform } = useElement({
    formOption: { formInfo },
    getExtraDicts: store.requestExtraDicts.bind(store)
  })
  // console.log('DictPositionModule:', toJS(store.dictMap.DictPositionModule))

  const styles: any = labelWidth
    ? {
        '--form-max-content-width': `${toPx(580 - labelWidth)}px`,
        '--form-label-width': `${toPx(labelWidth)}px`
      }
    : {}

  const renderFormInfoProps = (formInfoItemId: string) => {
    const _props: any = {}
    if (ScreenShotEnum.includes(formInfoItemId)) {
      _props['ref'] = screenShotRef
      return _props
    }
    return _props
  }
  useEffect(() => {
    convertValue(toJS(initialValues), null, convertFieldNames).then((_formated: any) => {
      setFormated(_formated)
      setTimeout(() => setRandom(Math.random()), 100)
    })
  }, [initialValues])
  useEffect(() => {
    screenShotRef.current && changeScreenShotRef(screenShotRef)
  }, [screenShotRef.current])
  // EditForm顶层注册setFormState用于向作用域useFunctionCall push数据源
  const eventManager = useEventManager()
  useEffect(() => {
    eventManager?.on('NewEditForm.setState', setState)
    eventManager?.on('NewEditForm.setFieldsValue', setFieldsValue)
    eventManager?.on('NewEditForm.resetFields', resetFields)
    return () => {
      eventManager?.off('NewEditForm.setState', setState)
      eventManager?.off('NewEditForm.setFieldsValue', setFieldsValue)
      eventManager?.off('NewEditForm.resetFields', resetFields)
    }
  }, [])
  console.log('NewEditForm._initialValues:', formated)
  const fragmentRander = () => {
    return (
      <>
        <div
          className={classNames('new-edit-form-inner', { 'new-edit-form-inner-readed': readed })}
          style={styles}
        >
          {formInfo.map((item: any, index: number) => {
            const {
              title = '',
              titleProps = {},
              form_list = [],
              is_show_title,
              is_show_delete,
              deleteButton,
              hide
            } = item
            const hasFormList = form_list?.length > 0
            // is_show_title 是否显示标题不展示
            const showTitle = title && is_show_title !== false && hasFormList

            const showDelete = is_show_delete && deleteButton
            return (
              !hideExec(hide, formated, {}) && (
                <Fragment key={`${item.key}_${item.id}_${index}`}>
                  {!(showDelete || showTitle) ? null : (
                    <div className='form-title-wrapper'>
                      {showTitle && (
                        <div {...titleProps} className='form-title'>
                          {title}
                        </div>
                      )}
                      {showDelete && (
                        <div
                          className='form-title-button'
                          onClick={() => deleteButton?.onClick?.(item.key || item.id)}
                        >
                          {deleteButton?.text}
                        </div>
                      )}
                    </div>
                  )}
                  <div
                    className={classNames(
                      'new-edit-form-item',
                      { 'new-edit-form-item-readed': readed },
                      item?.className || '',
                      `new-edit-form-item-id-${item?.id}`
                    )}
                    {...renderFormInfoProps(item?.id)}
                  >
                    {form_list.map((x: any, index: any) => (
                      <Fragment key={index}>
                        {!hideExec(x?.hide, state, {}) ? (
                          !x?.name && !x?.formItemProps?.name ? (
                            <RenderNode
                              type={x.type}
                              elementProps={getElementProps(x)}
                              stateStore={stateStore}
                            />
                          ) : (
                            <Form.Item key={index} {...getFormItemProps(x)}>
                              <RenderNode
                                type={x.type}
                                elementProps={getElementProps(x)}
                                stateStore={stateStore}
                              />
                            </Form.Item>
                          )
                        ) : null}
                      </Fragment>
                    ))}
                  </div>
                </Fragment>
              )
            )
          })}
          {randomForm === 1 || random === 1}
          {Object.keys(store.dictMap).find(() => false)}
        </div>
        {formButtons?.filter((i: any) => getCodeFun(i.code)).length > 0 && (
          <>
            <div
              className={classNames('new-edit-form-btns isFixed', {
                justone: formButtons?.length === 1
              })}
            >
              {formButtons?.map(
                (x, index) =>
                  getCodeFun(x.code) && (
                    <Button
                      {...x}
                      fill={x?.icon ? 'none' : x?.fill}
                      className={classNames(
                        'btn',
                        x.className || formButtonPreset[x.key]?.className || 'add-btn',
                        {
                          'unified-page-btn-icon': x?.icon
                        }
                      )}
                      key={x.key}
                      onClick={() => {
                        x?.onClick ? x.onClick() : formButtonPreset[x.key]?.onClick?.()
                      }}
                      loading={x?.loading || state?.loading}
                    >
                      {x?.icon && (
                        <SvgIcon
                          key={`${x?.icon}_${index}`}
                          name={x?.icon}
                          className='unified-page-btn-icon'
                        />
                      )}
                      <span
                        key={`${x.title || formButtonPreset[x.key]?.title}_${index}`}
                        className='unified-page-btn-text'
                      >
                        {x.title || formButtonPreset[x.key]?.title}
                      </span>
                    </Button>
                  )
              )}
            </div>
            <div className='new-edit-form-blank' />
            <SafeArea position='bottom' />
          </>
        )}
      </>
    )
  }
  if (!formated && initialValues) {
    return <Loading className='init-loading' />
  }
  console.log(
    formButtons?.filter((i: any) => getCodeFun(i.code)).length > 0,
    'formButtons',
    formButtons
  )
  return (
    <div className={classNames('new-edit', className, { 'new-edit-readed': readed })}>
      {formType === 'item' ? (
        fragmentRander()
      ) : (
        <Form
          key={formKey}
          form={formInstance}
          onFinish={formSubmit}
          onFinishFailed={formSubmitFailed}
          initialValues={formated}
          className={classNames('new-edit-form', { disabled })}
          onValuesChange={(e) => {
            console.log('onValuesChange', e)
            onValuesChange?.(e)
            setRandom(Math.random())
          }}
          layout='horizontal'
          {...rest}
        >
          {fragmentRander()}
          {randomForm === 1 || random === 1}
          {Object.entries(store.dictMap) === undefined}
        </Form>
      )}
    </div>
  )
})

export default observer(NewEditForm)
